import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import styles from './HeaderPanels.module.css'
import { storyMetaLine } from 'lib/editorial/types'
import type { HeaderNavStory } from 'lib/header-nav/types'

interface StoryCardProps {
  story: HeaderNavStory
  /**
   * `category` reads "Reviews · 2d ago" (category panels — the hub is the useful
   * context there); `author` reads "Dave Carr · 2d ago" (editorial columns, where the
   * hub is already the column heading). This is why the document ships the parts
   * separately instead of one baked meta string.
   */
  meta: 'category' | 'author'
  /** Editorial columns use a wider thumb and carry the review score badge. */
  variant?: 'default' | 'editorial'
  onNavigate?: () => void
}

const StoryCard: React.VFC<StoryCardProps> = ({ story, meta, variant = 'default', onNavigate }) => {
  const editorial = variant === 'editorial'
  const lead = meta === 'author' ? story.author : story.category
  const line = storyMetaLine(lead, story.age)

  return (
    <Linked
      href={story.url}
      className={clsx(styles.story, { [styles.storyEditorial]: editorial })}
      onClick={onNavigate}
    >
      <span className={clsx(styles.storyThumb, { [styles.storyThumbWide]: editorial })}>
        {story.imageUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={story.imageUrl} alt="" loading="lazy" />
        )}
        {editorial && story.score !== null && (
          <span className={styles.storyScore}>{story.score.toFixed(1)}</span>
        )}
      </span>
      <span className={styles.storyBody}>
        <span className={styles.storyTitle}>{story.title}</span>
        {line && <span className={styles.storyMeta}>{line}</span>}
      </span>
    </Linked>
  )
}

/**
 * The loading stand-in for a StoryCard: the same flex boxes (thumb + title + meta) with
 * pulsing fills, so the swap to real content moves nothing. Rendered only during the
 * first-visit window before `/api/header-nav` resolves.
 */
export const StorySkeleton: React.VFC<{ variant?: 'default' | 'editorial' }> = ({
  variant = 'default',
}) => {
  const editorial = variant === 'editorial'
  return (
    <span className={clsx(styles.story, { [styles.storyEditorial]: editorial })} aria-hidden="true">
      <span
        className={clsx(styles.storyThumb, styles.skeleton, {
          [styles.storyThumbWide]: editorial,
        })}
      />
      <span className={styles.storyBody}>
        <span className={clsx(styles.skeleton, styles.skeletonTitle)} />
        <span className={clsx(styles.skeleton, styles.skeletonMeta)} />
      </span>
    </span>
  )
}

export default StoryCard
